import { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { MOCK_IN_RUN_CREWING } from '../mock/mockData';
import CPMockBadge from './CPMockBadge';

const IN_RUN_ROOM_TYPES = [
  'High Speed 1', 'High Speed 2', 'Medium Speed',
  'Low Speed 1', 'Low Speed 2', 'Overlay', 'SAPS',
] as const;
type InRunRoomType = (typeof IN_RUN_ROOM_TYPES)[number];
type CrewingRow = { PA: number; MO: number; QI: number };

const C = { primary: '#522d6e', primaryLight: '#7c5aa4', white: '#ffffff', grey: '#e5e7eb', greyLight: '#f3f4f6', greyDark: '#6b7280', rowAlt: '#F7F3FE' };

const CPInRunCrewingTab = observer(() => {
  const store = useCapacityStore();
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState<Record<InRunRoomType, CrewingRow>>(
    () => Object.fromEntries(IN_RUN_ROOM_TYPES.map(rt => [rt, { ...(MOCK_IN_RUN_CREWING[rt] ?? { PA: 1, MO: 1, QI: 1 }) }])) as Record<InRunRoomType, CrewingRow>
  );
  const [original, setOriginal] = useState<Record<InRunRoomType, CrewingRow>>(() =>
    Object.fromEntries(IN_RUN_ROOM_TYPES.map(rt => [rt, { ...(MOCK_IN_RUN_CREWING[rt] ?? { PA: 1, MO: 1, QI: 1 }) }])) as Record<InRunRoomType, CrewingRow>
  );
  const [rawValues, setRawValues] = useState<Record<string, string>>({});

  const handleEdit = () => {
    const raw: Record<string, string> = {};
    IN_RUN_ROOM_TYPES.forEach(rt => {
      (['PA', 'MO', 'QI'] as const).forEach(role => {
        raw[`${rt}_${role}`] = String(rows[rt]?.[role] ?? 0);
      });
    });
    setRawValues(raw);
    setOriginal(JSON.parse(JSON.stringify(rows)));
    setEditing(true);
  };
  const handleCancel = () => { setRawValues({}); setRows(JSON.parse(JSON.stringify(original))); setEditing(false); };
  const handleChange = (cellId: string, raw: string) => {
    if (!/^(\d*\.?\d*)$/.test(raw)) return;
    setRawValues(prev => ({ ...prev, [cellId]: raw }));
  };
  const handleBlur = (cellId: string, rt: InRunRoomType, role: keyof CrewingRow) => {
    const raw = rawValues[cellId] ?? '';
    const v = parseFloat(raw);
    const val = Number.isNaN(v) ? 0 : v;
    setRows(prev => ({ ...prev, [rt]: { ...prev[rt], [role]: val } }));
    setRawValues(prev => ({ ...prev, [cellId]: String(val) }));
  };

  const handleSave = useCallback(async () => {
    // Flush any pending raw values into rows before saving
    setRows(prev => {
      const next = { ...prev } as Record<InRunRoomType, CrewingRow>;
      Object.entries(rawValues).forEach(([cellId, raw]) => {
        const lastUnderscore = cellId.lastIndexOf('_');
        const rt = cellId.slice(0, lastUnderscore) as InRunRoomType;
        const role = cellId.slice(lastUnderscore + 1) as keyof CrewingRow;
        const v = parseFloat(raw);
        const val = Number.isNaN(v) ? 0 : v;
        next[rt] = { ...next[rt], [role]: val };
      });
      return next;
    });
    setRawValues({});
    setEditing(false);
    setIsSaving(true);
    try {
      await store.applyPlanningSettings(store.startYearMonth, store.planningParams);
      toast.success('In-run crewing updated and dashboard recalculated.', { position: 'bottom-right', autoClose: 4000 });
    } finally {
      setIsSaving(false);
    }
  }, [store]);

  const actionButtons = (
    <div style={{ display: 'flex', gap: 8 }}>
      {editing ? (
        <>
          <Button label="Cancel" icon="pi pi-times" size="small" severity="secondary" onClick={handleCancel} />
          <Button label={isSaving ? 'Updating…' : 'Save & Update Dashboard'} icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} size="small" style={{ background: C.primary, borderColor: C.primary }} onClick={handleSave} disabled={isSaving} />
        </>
      ) : (
        <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" onClick={handleEdit} />
      )}
    </div>
  );

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontSize: 13 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '0.75rem 1rem', background: C.white, borderRadius: 8, border: `1px solid ${C.grey}`, gap: '1rem', flexWrap: 'wrap' as const }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <CPMockBadge />
          <i className="pi pi-users" style={{ color: C.primary }} />
          <span style={{ fontWeight: 700, color: C.primary, fontSize: 13 }}>In-Run Crewing</span>
          <span style={{ fontSize: 11, color: C.greyDark }}>Headcount per role per room type during active runs</span>
        </div>
        {actionButtons}
      </div>

      {/* Table */}
      <div style={{ border: `1px solid ${C.grey}`, borderRadius: 8, background: C.white, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: C.primaryLight }}>
              <th style={{ color: C.white, fontWeight: 500, fontSize: 12, padding: '0.6rem 1rem', textAlign: 'left', whiteSpace: 'nowrap' as const }}>Room Type</th>
              {(['PA', 'MO', 'QI'] as const).map(r => (
                <th key={r} style={{ color: C.white, fontWeight: 500, fontSize: 12, padding: '0.6rem 1rem', textAlign: 'center', width: 130 }}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {IN_RUN_ROOM_TYPES.map((rt, idx) => (
              <tr key={rt} style={{ background: idx % 2 ? C.rowAlt : C.white }}>
                <td style={{ padding: '0.5rem 1rem', fontWeight: 600, color: '#1e293b', borderBottom: `1px solid ${C.greyLight}` }}>{rt}</td>
                {(['PA', 'MO', 'QI'] as const).map(role => (
                  <td key={role} style={{ padding: '0.4rem 1rem', textAlign: 'center', borderBottom: `1px solid ${C.greyLight}` }}>
                    {editing ? (
                      <input type="text" inputMode="decimal" value={rawValues[`${rt}_${role}`] ?? ''}
                        onChange={e => handleChange(`${rt}_${role}`, e.target.value)}
                        onBlur={() => handleBlur(`${rt}_${role}`, rt, role)}
                        onFocus={() => { if (rawValues[`${rt}_${role}`] === '0') setRawValues(prev => ({ ...prev, [`${rt}_${role}`]: '' })); }}
                        style={{ width: 70, textAlign: 'center', fontSize: 12, padding: '3px 6px', border: `1px solid ${C.grey}`, borderRadius: 4, outline: 'none' }}
                      />
                    ) : (
                      <span style={{ fontWeight: 500, color: '#374151' }}>{rows[rt][role]}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: 8 }}>
          {actionButtons}
        </div>
      )}
    </div>
  );
});

export default CPInRunCrewingTab;
